import { useEffect, useMemo, useState } from "react";
import {
    Download,
    Plus,
    RefreshCw,
    Grid2X2,
    CheckCircle2,
    Star,
    Flame,
} from "lucide-react";

import {
    getCategories,
    addCategory,
    updateCategory,
    deleteCategory,
} from "../services/adminApi";

import CategoryFilters from "../components/category/CategoryFilters";
import CategoryTable from "../components/category/CategoryTable";
import CategoryForm from "../components/category/CategoryForm";

import "../css/Category.css";

function Category() {

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showForm, setShowForm] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [filters, setFilters] = useState({
        search: "",
        status: "",
        featured: "",
        popular: "",
    });

    const loadCategories = async () => {

        try {

            setLoading(true);

            const res = await getCategories();

            setCategories(res.data.categories || []);

        } catch (err) {

            console.error(err);

        } finally {

            setLoading(false);

        }

    };

    useEffect(() => {

        loadCategories();

    }, []);

    const handleFilterChange = (key, value) => {

        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));

    };

    const filteredCategories = useMemo(() => {

        let data = [...categories];

        if (filters.search) {

            const search = filters.search.toLowerCase();

            data = data.filter((category) =>
                category.name.toLowerCase().includes(search) ||
                category.slug?.toLowerCase().includes(search)
            );

        }

        if (filters.status !== "") {

            data = data.filter(
                (category) =>
                    category.isActive.toString() === filters.status
            );

        }

        if (filters.featured !== "") {

            data = data.filter(
                (category) =>
                    category.isFeatured.toString() === filters.featured
            );

        }

        if (filters.popular !== "") {

            data = data.filter(
                (category) =>
                    category.isPopular.toString() === filters.popular
            );

        }

        return data;

    }, [categories, filters]);

    const openAddModal = () => {

        setSelectedCategory(null);
        setShowForm(true);

    };

    const openEditModal = (category) => {

        setSelectedCategory(category);
        setShowForm(true);

    };

    const closeModal = () => {

        setSelectedCategory(null);
        setShowForm(false);

    };

    return (

        <div className="category-page">

            <div className="category-header">

                <div>

                    <h1>Category Management</h1>

                    <p>
                        Manage all food categories.
                    </p>

                </div>

                <div className="category-actions">

                    <button className="export-btn">

                        <Download size={18} />

                        Export

                    </button>

                    <button
                        className="refresh-btn"
                        onClick={loadCategories}
                    >

                        <RefreshCw size={18} />

                        Refresh

                    </button>

                    <button
                        className="add-btn"
                        onClick={openAddModal}
                    >

                        <Plus size={18} />

                        Add Category

                    </button>

                </div>

            </div>

            <div className="category-overview">

                <div className="overview-card">

                    <Grid2X2 size={24} />

                    <div>

                        <h2>{categories.length}</h2>

                        <p>Total</p>

                    </div>

                </div>

                <div className="overview-card">

                    <CheckCircle2 size={24} />

                    <div>

                        <h2>

                            {
                                categories.filter(
                                    c => c.isActive
                                ).length
                            }

                        </h2>

                        <p>Active</p>

                    </div>

                </div>

                <div className="overview-card">

                    <Star size={24} />

                    <div>

                        <h2>

                            {
                                categories.filter(
                                    c => c.isFeatured
                                ).length
                            }

                        </h2>

                        <p>Featured</p>

                    </div>

                </div>

                <div className="overview-card">

                    <Flame size={24} />

                    <div>

                        <h2>

                            {
                                categories.filter(
                                    c => c.isPopular
                                ).length
                            }

                        </h2>

                        <p>Popular</p>

                    </div>

                </div>

            </div>

            <CategoryFilters
                filters={filters}
                onChange={handleFilterChange}
            />

            <CategoryTable
                categories={filteredCategories}
                loading={loading}
                onEdit={openEditModal}
                onDelete={deleteCategory}
                reload={loadCategories}
            />

            {showForm && (

                <CategoryForm
                    category={selectedCategory}
                    addCategory={addCategory}
                    updateCategory={updateCategory}
                    reload={loadCategories}
                    onClose={closeModal}
                />

            )}

        </div>

    );

}

export default Category;